import { CardDef } from '../types.js';
import { showCardDetail } from './CardDetailView.js';

/** Show a fan of cards being opened. Each reveals with a 0.5s flip
 *  animation; cards in `newCardIds` get a "New!" badge that fades in
 *  mid-flip via a CSS transition with a 0.25s delay (timed to land
 *  just as the front face crosses the perpendicular and starts becoming
 *  visible). `newCardIds` is matched per-instance, not per-type, so a
 *  pack with two copies of a brand-new card shows the badge on both
 *  reveals — the player saw it for the first time twice.
 *
 *  When `onAddToDeck` is supplied, an extra "Add to Deck" button
 *  renders to the left of Continue once the reveals finish. Caller is
 *  responsible for routing to the deck builder. The intent is to
 *  short-circuit the path "open pack → main menu → deck builder" into
 *  a single tap, since players who just acquired cards usually want to
 *  slot them in. */
export function createPackOpeningView(
  container: HTMLElement,
  cardIds: string[],
  catalog: Map<string, CardDef>,
  onContinue: () => void,
  newCardIds: Set<string> = new Set(),
  onAddToDeck?: () => void,
): void {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'pack-opening';

  // The button row sits below the cards. Continue stays the rightmost
  // (the affirmative-default position); Add to Deck is rendered first
  // so it lands to its left visually. Both share `.pack-finish-btn` so
  // the reveal-complete handler can show them as a group.
  const addToDeckBtnHtml = onAddToDeck
    ? '<button class="menu-btn pack-finish-btn pack-add-to-deck-btn" style="display:none">Add to Deck</button>'
    : '';
  el.innerHTML = `
    <h2 class="pack-opening-title">New Cards!</h2>
    <div class="pack-cards"></div>
    <div class="pack-finish-row">
      ${addToDeckBtnHtml}
      <button class="menu-btn primary pack-finish-btn pack-continue-btn" style="display:none">Continue</button>
    </div>
  `;

  const cardsContainer = el.querySelector('.pack-cards')!;
  const continueBtn = el.querySelector('.pack-continue-btn') as HTMLButtonElement;
  const addToDeckBtn = el.querySelector('.pack-add-to-deck-btn') as HTMLButtonElement | null;

  // Create face-down cards
  const cardEls: HTMLElement[] = [];
  for (let i = 0; i < cardIds.length; i++) {
    const cardEl = document.createElement('div');
    cardEl.className = 'pack-card-reveal face-down';
    cardEl.innerHTML = '<div class="pack-card-back">?</div><div class="pack-card-front"></div>';
    cardsContainer.appendChild(cardEl);
    cardEls.push(cardEl);
  }

  // Reveal cards one at a time
  let revealed = 0;
  function revealNext() {
    if (revealed >= cardIds.length) {
      continueBtn.style.display = '';
      if (addToDeckBtn) addToDeckBtn.style.display = '';
      return;
    }

    const cardEl = cardEls[revealed];
    const cardId = cardIds[revealed];
    const def = catalog.get(cardId);
    if (!def) { revealed++; revealNext(); return; }

    const front = cardEl.querySelector('.pack-card-front')!;
    const artHtml = def.art
      ? `<img src="${def.art}" alt="${def.name}" class="pack-reveal-art" onerror="this.style.display='none'" />`
      : '';

    // The badge sits inside the front face so it rotates with it during
    // the flip; its own opacity/transform transition (with a 0.25s
    // delay) handles the mid-flip fade-in.
    const newBadgeHtml = newCardIds.has(cardId)
      ? '<div class="pack-reveal-new-badge">New!</div>'
      : '';

    front.innerHTML = `
      ${artHtml}
      ${newBadgeHtml}
      <div class="pack-reveal-name">${def.name}</div>
      <div class="pack-reveal-rarity">${def.rarity}</div>
    `;

    cardEl.dataset.rarity = def.rarity;
    cardEl.classList.remove('face-down');
    cardEl.classList.add('revealed');
    // Tap-to-inspect: opens the full card-detail modal (same one the
    // in-game log uses when clicking a card-name link). Hover tooltip
    // was previously attached here — replaced with the modal because
    // the reveal tile shows only art + name + rarity, so players who
    // want to read the ability text need a fuller surface than a
    // tooltip's narrow strip.
    cardEl.addEventListener('click', () => showCardDetail(cardId, catalog));

    revealed++;
    setTimeout(revealNext, 500);
  }

  setTimeout(revealNext, 300);

  continueBtn.addEventListener('click', onContinue);
  if (addToDeckBtn && onAddToDeck) {
    addToDeckBtn.addEventListener('click', onAddToDeck);
  }
  container.appendChild(el);
}
